
import { useEffect, useState } from "react";
import { useNavigate } from "react-router";
import { Box, LinearProgress, Tooltip, styled, } from "@mui/material";
import { GridApiPro, GridColDef, GridColumnVisibilityModel } from "@mui/x-data-grid-pro";
import { DateFilterOperator, NumberFilterOperator, StatusFilterOperator, TaskProgressFilterOperator, TextFilterOperator } from "components/shared/filters";

import { TaskProgressPill } from "components/tasks";
import { ProjectStatus } from "models/projects";
import moment from "moment";
import { formatDate } from "utils/date-utils";
import useGridColumns, { ColumnVisibilityOrOrderChangeHandler } from "hooks/shared/useGridColumns";
import { IDashboardSettings } from "components/preferences";
import { SetPreferenceSignature } from "hooks/preferences";
import PercentageFilterOperator from "components/shared/filters/PercentageFilterOperator";
import { SC_Constants } from "utils/sc-constants";

const LinearProgressTasks = styled(LinearProgress)(({ theme }) => ({
  backgroundColor: '#FFFFFF',
  border: '#888888 1px solid',
  borderRadius: '10px',
  height: '12px',
  '.MuiLinearProgress-bar': {
    backgroundColor: '#888888'
  }
}));

type UseProjectColumnsProps = {
  status: ProjectStatus,
  all: boolean,
  settings?: IDashboardSettings | null,
  setSettings?: SetPreferenceSignature,
};

type UseProjectColumnsResult = [
  GridColDef[],
  GridColumnVisibilityModel,
  ColumnVisibilityOrOrderChangeHandler,
];

type UseProjectColumnsSignature = (props: UseProjectColumnsProps) => UseProjectColumnsResult;

const useProjectColumns: UseProjectColumnsSignature = (props) => {
  const { status, all } = props;
  const { settings, setSettings } = props;
  const columnBase: GridColDef[] = [
    {
      field: 'id', headerName: 'WI ID',  width: 150, sortable: true, resizable: false, hideable: false, hideSortIcons: false, disableReorder: true,
      filterOperators: NumberFilterOperator,
      renderHeader: (params) => {
        return (
          <Tooltip title="Work Item ID" arrow>
            <div>{"WI ID"}</div>
          </Tooltip>
        )
      }
    },
    {
      field: 'name', headerName: 'Project Title', flex: 1, minWidth: 300, sortable: true, resizable: true, hideable: false, headerClassName: 'header', hideSortIcons: false, disableReorder: true,
      filterOperators: TextFilterOperator,
      renderCell: (params) => {
        const name = params.value;
        const projectId = params.row.id;
        return (
           <Tooltip title={name} arrow>
             <div>
               <a onClick={() => navigateToProject(projectId)} style={{ color: '#0066CC', cursor: 'pointer', textDecoration: 'underline' }} rel="noopener noreferrer">
                 {name}
               </a>
             </div>
           </Tooltip>
        );
      }
    },
    {
      field: 'startDate', headerName: 'Start Date', minWidth: 120, width: 200, sortable: true, resizable: true, hideable: true, headerClassName: 'header', hideSortIcons: false, type: 'date',
      valueGetter: (value) => {
        return !value ? null : moment.utc(value).toDate();
      },
      filterOperators: DateFilterOperator,
      renderCell: (params) => {
        const startDate = params.value;
        return (
          <div>
            {formatDate(startDate)}
          </div>
        );
      }
    },
    {
      field: 'endDate', headerName: 'End Date', minWidth: 120, width: 200, sortable: true, resizable: true, hideable: true, headerClassName: 'header', hideSortIcons: false, type: 'date',
      valueGetter: (value) => {
        return !value ? null : moment.utc(value).toDate();
      },
      filterOperators: DateFilterOperator,
      renderCell: (params) => {
        const endDate = params.value;
        return (
          <div>
            {formatDate(endDate)}
          </div>
        );
      }
    },
    { field: 'sellingSeason', headerName: 'Selling Season', minWidth: 120, width: 200, sortable: true, resizable: true, hideable: true, headerClassName: 'header', hideSortIcons: false, filterOperators: TextFilterOperator },
    { field: 'milestoneName', headerName: 'Milestone Task', minWidth: 120, width: 200, sortable: true, resizable: true, hideable: true, headerClassName: 'header', hideSortIcons: false, filterOperators: TextFilterOperator },
    {
      field: 'milestoneDate', headerName: 'Milestone Date', minWidth: 120, width: 200, sortable: true, resizable: true, hideable: true, headerClassName: 'header', hideSortIcons: false, type: 'date',
      valueGetter: (value) => {
        return !value ? null : moment.utc(value).toDate();
      },
      filterOperators: DateFilterOperator,
      renderCell: (params) => {
        const milestoneDate = params.value;
        return (
          <div>
            {formatDate(milestoneDate)}
          </div>
        );
      }
    },
    { field: 'status', headerName: 'Status', minWidth: 120, width: 200, sortable: true, resizable: true, hideable: false, headerClassName: 'header', hideSortIcons: false, filterOperators: StatusFilterOperator },
    { field: 'workspaceName', headerName: 'Workspace', minWidth: 120, width: 200, sortable: true, resizable: true, hideable: true, headerClassName: 'header', hideSortIcons: false, filterOperators: TextFilterOperator },
    {
      field: 'progress', headerName: 'Progress', minWidth: 120, width: 200, sortable: true, resizable: true, hideable: true, headerClassName: 'header', hideSortIcons: false,
      filterOperators: TaskProgressFilterOperator(true),
      renderCell: (params) => {
        const projectStatus = params.row.status;
        return projectStatus && String(projectStatus).toLowerCase() === 'in progress' && params.value != null ? (<TaskProgressPill progress={params.value} />) : null;
      }
    },
    {
      field: 'completedTasksPercentage', headerName: 'Tasks Progress', minWidth: 120, width: 200, sortable: true, resizable: true, hideable: true, headerClassName: 'header', hideSortIcons: false,
      filterOperators: PercentageFilterOperator,
      renderCell: (params) => {
        return (
          <Box sx={{ ':hover .MuiLinearProgress-bar': { backgroundColor: '#555555' } }}>
            <Tooltip title={`${params.row.completedTaskCount} / ${params.row.taskCount} tasks completed`} arrow>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Box sx={{ width: '80px', mr: 1 }}>
                  <LinearProgressTasks variant="determinate" value={params.row.completedTasksPercentage * 100} />
                </Box>
                <Box sx={{ minWidth: 35 }}>
                  {`${Math.round(params.row.completedTasksPercentage * 100)}%`}
                </Box>
              </Box>
            </Tooltip>
          </Box>
        );
      }
    },
    { field: 'contentType', headerName: 'Content Type', minWidth: 120, width: 200, sortable: true, resizable: true, hideable: true, headerClassName: 'header', hideSortIcons: false, filterOperators: TextFilterOperator }
  ];

  const navigate = useNavigate();
  const [columns, setColumns] = useState<GridColDef[]>(columnBase);

  const navigateToProject = (projectId: number) => {
     localStorage.setItem(SC_Constants.projectProgress, status);
    const allSegment = all ? 'all' : 'my';
    navigate(`/projects/${allSegment}/${projectId}/overview/`);
  };

  const [columnsModel, visibilityModel, handleColumnVisibilityOrOrderChange] = useGridColumns({ columns: columns, settings, setSettings })

  useEffect(() => {
    let newColumns: GridColDef[] = [];
    newColumns.push(...columnBase);

    if (!all) {
      const workspaceIdx = newColumns.findIndex(c => c.field === 'workspaceName');
      if (workspaceIdx > -1) {
        newColumns.splice(workspaceIdx, 1);
      }
    }

    if (status !== 'all') {
      const statusIdx = newColumns.findIndex((c) => c.field === 'status');
      if (statusIdx > -1) {
        newColumns.splice(statusIdx, 1);
      }
    }

    if (status !== 'all' && status !== 'in-progress') {
      const progressIdx = newColumns.findIndex((c) => c.field === 'progress');
      if (progressIdx > -1) {
        newColumns.splice(progressIdx, 1);
      }
    }

    if (all) {
      const completedTaskPerc = newColumns.findIndex(c => c.field === 'completedTasksPercentage');
      if (completedTaskPerc > -1) {
        newColumns.splice(completedTaskPerc, 1);
      }
    }

    setColumns(newColumns);
  }, [status, all]);

  return [
    columnsModel,
    visibilityModel,
    handleColumnVisibilityOrOrderChange
  ];
};

export default useProjectColumns;